import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import EmployeeCornerSidebar from "./EmployeeCornerSidebar";

// Helper to format YYYY-MM-DD into DD-MM-YYYY
const formatDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

// Helper to format date and time into DD-MM-YYYY format including seconds
const formatDateTime = (dateVal) => {
  if (!dateVal) return "";
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return dateVal;
  
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const timeStr = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });

  return `${day}-${month}-${year}, ${timeStr}`;
};

const EmployeeAttendanceRequest = () => {
  const loggedUser = JSON.parse(localStorage.getItem("employeeUser")) || {};
  const token = localStorage.getItem("employeeToken");

  const [activeTab, setActiveTab] = useState("form"); // "form" or "list"
  const [monthYear, setMonthYear] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
  });

  const [absentDatesList, setAbsentDatesList] = useState([]);
  const [selectedAbsentDates, setSelectedAbsentDates] = useState([]);
  const [reason, setReason] = useState("");
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const employeeId = loggedUser.employeeID || "";
  const employeeUserId = loggedUser.employeeUserId || "";
  const employeeName = `${loggedUser.firstName || ""} ${loggedUser.lastName || ""}`.trim();

  // Fetch absent dates when monthYear changes
  useEffect(() => {
    if (!employeeId || !monthYear) return;
    fetchAbsentDates();
  }, [monthYear, employeeId, applications, editingId]);

  // Fetch application list on load
  useEffect(() => {
    if (employeeId) {
      fetchApplications();
    }
  }, [employeeId]);

  const fetchAbsentDates = async () => {
    try {
      let url = `/api/employee-attendance-requests/absent-dates/${employeeId}/${monthYear}`;
      if (isEditMode && editingId) {
        url += `?excludeId=${editingId}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        let dates = res.data.data || [];
        if (isEditMode && selectedAbsentDates.length > 0) {
          dates = Array.from(new Set([...dates, ...selectedAbsentDates]));
        }
        dates.sort((a, b) => new Date(a) - new Date(b));
        setAbsentDatesList(dates);
      }
    } catch (err) {
      setAbsentDatesList([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await axios.get(`/api/employee-attendance-requests/employee/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setApplications(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching requests list", err);
    }
  };

const handleCheckboxChange = (date) => {
  if (selectedAbsentDates.includes(date)) {
    // Allow deselecting the active date
    setSelectedAbsentDates([]);
  } else if (selectedAbsentDates.length >= 1) {
    // Show toast if they try to select a second date
    toast.error("Only 1 date per request allowed.");
  } else {
    setSelectedAbsentDates([date]);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
   if (selectedAbsentDates.length !== 1) {
  toast.error("Please select exactly one absent date per application.");
  return;
}

    setLoading(true);
    try {
      const payload = {
        employeeId,
        employeeUserId,
        name: employeeName,
        monthYear,
        absentDates: selectedAbsentDates,
        reason: reason.trim()
      };

      if (isEditMode) {
        const res = await axios.put(`/api/employee-attendance-requests/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          toast.success("Attendance request updated successfully!");
          setIsEditMode(false);
          setEditingId(null);
        }
      } else {
        const res = await axios.post("/api/employee-attendance-requests", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          toast.success("Attendance request submitted successfully!");
        }
      }

      setSelectedAbsentDates([]);
      setReason("");
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (app) => {
    setIsEditMode(true);
    setEditingId(app._id);
    setMonthYear(app.monthYear);
    setSelectedAbsentDates(app.absentDates || []);
    setReason(app.reason || "");
    setActiveTab("form");
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this attendance request?")) return;
    try {
      const res = await axios.delete(`/api/employee-attendance-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        toast.success("Attendance request deleted successfully!");
        fetchApplications();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const inputClass =
    "w-full pl-2 pr-2 py-1.5 sm:py-1 border border-gray-300 rounded text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-sky-500 transition-all duration-150";

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <EmployeeCornerSidebar /> 

      <div className="flex-1 p-2 sm:p-2 md:p-2">
        <div className="bg-white p-2 sm:p-2 md:p-2 rounded-xl shadow-lg w-full mx-auto">
          
          {/* Top Employee Info Banner & Navigation Switch */}
          <div className="mb-1 p-1 bg-sky-50 border border-sky-200 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-base sm:text-lg font-bold text-sky-900">
                Employee Name: <span className="font-normal">{employeeName || "Loading..."}</span>
              </h2>
              <p className="text-xs sm:text-sm text-sky-700 font-medium">
                Employee ID: <span className="font-bold">{employeeUserId || employeeId}</span>
              </p>
            </div>
            
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setActiveTab("form")}
                className={`flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-colors ${
                  activeTab === "form" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Apply Request
              </button>
              <button
                onClick={() => setActiveTab("list")}
                className={`flex-1 sm:flex-none px-4 py-2 text-xs sm:text-sm font-bold rounded-lg transition-colors ${
                  activeTab === "list" ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                View List
              </button>
            </div>
          </div>

          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-1 border-b pb-1">
            {activeTab === "form" ? (isEditMode ? "Edit Attendance Request" : "Employee Attendance Request") : "Attendance Request Applications List"}
          </h2>

          {/* Form View */}
          {activeTab === "form" && (
            <form onSubmit={handleSubmit} className="space-y-2">
              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700">Select Month & Year</label>
                <input
                  type="month"
                  value={monthYear}
                  onChange={(e) => setMonthYear(e.target.value)}
                  className={inputClass}
                  required
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                  Absent Dates (Selected Month Only)
                </label>
                {absentDatesList.length === 0 ? (
                  <p className="text-xs sm:text-sm text-gray-500 italic bg-gray-50 p-3 rounded border">
                    No available absent dates recorded for {monthYear} (or submission deadline passed).
                  </p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-8 gap-2 bg-gray-50 p-2 rounded border">
                    {absentDatesList.map((date) => (
                      <label
                        key={date}
                        className="flex items-center space-x-2 p-1 bg-white border rounded shadow-sm hover:bg-sky-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedAbsentDates.includes(date)}
                          onChange={() => handleCheckboxChange(date)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-sky-400"
                        />
                        <span className="text-xs sm:text-sm text-gray-800 font-medium">
                          {formatDDMMYYYY(date)}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-bold text-gray-700">Reason</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows="3"
                  className={`${inputClass} h-16 sm:h-20 p-2`}
                  placeholder="Provide reason for attendance regularization..."
                  required
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                {isEditMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditMode(false);
                      setEditingId(null);
                      setSelectedAbsentDates([]);
                      setReason("");
                    }}
                    className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded-lg font-bold transition-colors text-xs sm:text-sm"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 sm:px-8 py-2 rounded-lg font-bold transition-colors"
                >
                  {loading ? "Saving..." : isEditMode ? "Update Request" : "Submit Attendance Request"}
                </button>
              </div>
            </form>
          )}

          {/* List View */}
          {activeTab === "list" && (
            <div>
              {applications.length === 0 ? (
                <p className="text-xs sm:text-sm text-gray-500 italic text-center py-6">
                  No attendance request applications found.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto border border-dorika-blue rounded-lg overflow-hidden">
                    <thead className="bg-dorika-blue text-white">
                      <tr>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">Applied On</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">Y/M</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">Absent Dates</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">Reason</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">RM Status</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">HR Status</th>
                        <th className="px-4 text-left text-xs font-bold uppercase tracking-wider border border-dorika-blue">Final Status</th>
                        <th className="px-4 text-center text-xs font-bold uppercase tracking-wider border border-dorika-blue">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white text-xs sm:text-sm">
                      {applications.map((app) => {
                        // Lock edit and delete if Manager, HR, or Final Status is no longer Pending (i.e. Approved or Rejected)
                        const isActioned = app.reportingManagerStatus !== "Pending" || app.HRStatus !== "Pending" || app.FinalStatus !== "Pending";

                        return (
                          <tr key={app._id} className="hover:bg-gray-50">
                            <td className="px-4 whitespace-nowrap text-gray-800 border border-dorika-blue">{formatDateTime(app.applicationDate)}</td>
                            <td className="px-4 whitespace-nowrap font-medium text-gray-900 border border-dorika-blue">{app.monthYear}</td>
                            <td className="px-4 text-gray-700 border border-dorika-blue">
                              <div className="flex flex-wrap gap-1">
                                {app.absentDates?.map((d) => (
                                  <span key={d} className="px-0.5 bg-sky-100 text-sky-800 rounded font-semibold text-xs border border-sky-200">
                                    {formatDDMMYYYY(d)}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 text-gray-600 max-w-xs truncate border border-dorika-blue">{app.reason}</td>
                            
                            {/* Manager Status & Timestamp with Seconds */}
                            <td className="px-4 whitespace-nowrap border border-dorika-blue">
                              <span className={`px-1 inline-flex text-xs font-bold rounded ${
                                app.reportingManagerStatus === "Approved" ? "bg-green-100 text-green-800" :
                                app.reportingManagerStatus === "Rejected" ? "bg-red-100 text-red-800" :
                                "bg-yellow-100 text-yellow-800"
                              }`}>
                                {app.reportingManagerStatus || "Pending"}
                              </span>
                              {app.reportingManagerStatusDate && (
                                <div className="text-[10px] text-gray-800 mt-0.5">
                                  {app.reportingManagerStatusDate} {app.reportingManagerStatusTime}
                                </div>
                              )}
                            </td>

                            {/* HR Status & Timestamp with Seconds */}
                            <td className="px-4 whitespace-nowrap border border-dorika-blue">
                              <span className={`px-1 inline-flex text-xs font-bold rounded ${
                                app.HRStatus === "Approved" ? "bg-green-100 text-green-800" :
                                app.HRStatus === "Rejected" ? "bg-red-100 text-red-800" :
                                "bg-yellow-100 text-yellow-800"
                              }`}>
                                {app.HRStatus || "Pending"}
                              </span>
                              {app.HRStatusDate && (
                                <div className="text-[10px] text-gray-800 mt-0.5">
                                  {app.HRStatusDate} {app.HRStatusTime}
                                </div>
                              )}
                            </td>

                            {/* Final Status */}
                            <td className="px-4 whitespace-nowrap border border-dorika-blue">
                              <span className={`px-1 inline-flex text-xs font-bold rounded-full ${
                                app.FinalStatus === "Approved" ? "bg-green-100 text-green-800" :
                                app.FinalStatus === "Rejected" ? "bg-red-100 text-red-800" :
                                "bg-yellow-100 text-yellow-800"
                              }`}>
                                {app.FinalStatus || "Pending"}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-4 whitespace-nowrap text-center space-x-2 border border-dorika-blue">
                              {!isActioned ? (
                                <>
                                  <button
                                    onClick={() => handleEditClick(app)}
                                    className="px-3 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded font-bold text-xs transition-colors"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(app._id)}
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded font-bold text-xs transition-colors"
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400 italic">Locked</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendanceRequest;